import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Loader2,
  CheckCircle2,
  TriangleAlert,
  FileText,
  Clock,
  Check,
  X,
} from "lucide-react";
import { getExcelProgress } from "../api/excelProgressAPI";
import useProjectEventsSSE from "../hooks/useProjectEventsSSE";

const TRACKER_THEMES = {
  in_progress: {
    accent: "#d97706",
    soft: "#fffbeb",
    border: "#fde68a",
    iconBg: "#fef3c7",
    track: "#fde68a",
    pillBg: "#fef3c7",
    pillText: "#b45309",
  },
  completed: {
    accent: "#059669",
    soft: "#ecfdf5",
    border: "#a7f3d0",
    iconBg: "#d1fae5",
    track: "#a7f3d0",
    pillBg: "#d1fae5",
    pillText: "#047857",
  },
  partially_failed: {
    accent: "#ea580c",
    soft: "#fff7ed",
    border: "#fed7aa",
    iconBg: "#ffedd5",
    track: "#fed7aa",
    pillBg: "#ffedd5",
    pillText: "#c2410c",
  },
  failed: {
    accent: "#dc2626",
    soft: "#fef2f2",
    border: "#fecaca",
    iconBg: "#fee2e2",
    track: "#fecaca",
    pillBg: "#fee2e2",
    pillText: "#b91c1c",
  },
};

const formatEta = (minutes) => {
  if (minutes == null || minutes <= 0) return "Finishing up";
  if (minutes === 1) return "Estimated completion in about 1 min";
  return `Estimated completion in about ${minutes} mins`;
};

const resolveTrackerView = (progress) => {
  if (!progress || progress.status == null) return null;

  const status = progress.status;
  const totalChunks = progress.total_chunks;
  const preparing = status === "in_progress" && totalChunks == null;

  const theme = TRACKER_THEMES[status];
  if (!theme) return null;

  let pct = null;
  if (!preparing) {
    const raw =
      progress.progress_percentage != null
        ? progress.progress_percentage
        : totalChunks && progress.completed_chunks != null
        ? (progress.completed_chunks / totalChunks) * 100
        : 0;
    pct = Math.max(0, Math.min(100, Math.round(raw)));
  }

  const terminalLabels = {
    completed: "Completed",
    partially_failed: "Completed with errors",
    failed: "Failed",
  };
  const label = preparing
    ? "Preparing"
    : status === "in_progress"
    ? "In Progress"
    : terminalLabels[status];

  let etaLabel;
  if (preparing) etaLabel = "Preparing the file for processing";
  else if (status === "in_progress")
    etaLabel = formatEta(progress.estimated_completion_minutes);
  else if (status === "completed")
    etaLabel = "Processing complete — all households created";
  else if (status === "partially_failed") etaLabel = "Completed with errors";
  else etaLabel = "Processing stopped before completion";

  const isTerminal =
    status === "completed" ||
    status === "partially_failed" ||
    status === "failed";

  return {
    status,
    preparing,
    theme,
    pct,
    label,
    etaLabel,
    indeterminate: preparing,
    showPercent: !preparing,
    dismissible: isTerminal,
    spinning: preparing || status === "in_progress",
    iconType:
      preparing || status === "in_progress"
        ? "spinner"
        : status === "completed"
        ? "success"
        : "alert",
    createdCount: progress.created_households_count || 0,
    failedCount: progress.failed_households_count || 0,
    fileName: progress.file_name || "Excel file",
    batchId: progress.batch_id || null,
  };
};

const getAdvisorToken = () => {
  try {
    return (
      JSON.parse(localStorage.getItem("userData") || "{}").access_token || null
    );
  } catch (err) {
    return null;
  }
};

const ExcelProcessingTracker = ({ projectId, triggerToken }) => {
  const [progress, setProgress] = useState(null);
  const [dismissedBatchId, setDismissedBatchId] = useState(null);
  const token = useMemo(() => getAdvisorToken(), []);

  useEffect(() => {
    if (!projectId) return undefined;
    let cancelled = false;
    getExcelProgress(projectId)
      .then((data) => {
        if (!cancelled) setProgress(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [projectId, triggerToken]);

  useEffect(() => {
    setDismissedBatchId(null);
  }, [triggerToken]);

  const handleEvent = useCallback(
    (evt) => {
      if (
        evt &&
        evt.event === "bulk_upload_progress" &&
        (!evt.project_id || evt.project_id === projectId)
      ) {
        setProgress(evt);
      }
    },
    [projectId]
  );

  useProjectEventsSSE({
    projectId,
    token,
    enabled: !!token && !!projectId,
    onEvent: handleEvent,
  });

  const view = resolveTrackerView(progress);
  if (!view) return null;
  if (view.dismissible && view.batchId && dismissedBatchId === view.batchId) {
    return null;
  }

  const { theme } = view;

  return (
    <div
      className="mb-4 rounded-2xl overflow-hidden border shadow-sm"
      style={{ borderColor: theme.border, background: theme.soft }}
    >
      <style>{`@keyframes ft-tracker-shimmer{0%{transform:translateX(-100%)}100%{transform:translateX(220%)}}`}</style>
      <div className="flex">
        <div
          className="w-[5px] flex-shrink-0"
          style={{ background: theme.accent }}
        />
        <div className="flex-1 px-5 py-[18px]">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div
                className="w-10 h-10 rounded-xl flex-shrink-0 flex items-center justify-center"
                style={{ background: theme.iconBg, color: theme.accent }}
              >
                {view.iconType === "spinner" && (
                  <Loader2 className="w-5 h-5 animate-spin" />
                )}
                {view.iconType === "success" && (
                  <CheckCircle2 className="w-[22px] h-[22px]" />
                )}
                {view.iconType === "alert" && (
                  <TriangleAlert className="w-[22px] h-[22px]" />
                )}
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                    Excel Processing
                  </span>
                  <span
                    className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wide"
                    style={{ background: theme.pillBg, color: theme.pillText }}
                  >
                    <span
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: theme.accent }}
                    />
                    {view.label}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 mt-1 min-w-0">
                  <FileText className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  <span className="text-sm font-semibold text-gray-900 truncate">
                    {view.fileName}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {view.showPercent && (
                <div
                  className="text-2xl font-extrabold leading-none"
                  style={{ color: theme.accent }}
                >
                  {view.pct}%
                </div>
              )}
              {view.dismissible && (
                <button
                  type="button"
                  onClick={() => setDismissedBatchId(view.batchId)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Dismiss"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          <div
            className="relative mt-3.5 h-2.5 rounded-full overflow-hidden"
            style={{ background: theme.track }}
          >
            <div
              className="absolute inset-y-0 left-0 h-full rounded-full transition-[width] duration-500"
              style={{
                width: `${view.indeterminate ? 100 : view.pct}%`,
                background: theme.accent,
              }}
            >
              {view.spinning && (
                <div className="absolute inset-0 overflow-hidden rounded-full">
                  <div
                    className="absolute top-0 left-0 h-full w-2/5"
                    style={{
                      background:
                        "linear-gradient(90deg,transparent,rgba(255,255,255,.55),transparent)",
                      animation: "ft-tracker-shimmer 1.4s ease-in-out infinite",
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
            <div className="flex items-center gap-1.5 text-[13px] text-gray-600">
              <Clock
                className="w-[15px] h-[15px]"
                style={{ color: theme.accent }}
              />
              <span className="font-medium">{view.etaLabel}</span>
            </div>
            {!view.preparing &&
              (view.createdCount > 0 || view.failedCount > 0) && (
                <div className="flex items-center gap-2">
                  {view.createdCount > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">
                      <Check className="w-3 h-3" />
                      {view.createdCount} households created
                    </span>
                  )}
                  {view.failedCount > 0 && (
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-[3px] rounded-full text-xs font-semibold bg-red-100 text-red-700">
                      <X className="w-3 h-3" />
                      {view.failedCount} failed
                    </span>
                  )}
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelProcessingTracker;
